import { FC, CSSProperties } from 'react'
import dayjs from 'dayjs'
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownsPopupSettings,
  MultiSelect,
  MultiSelectChangeEvent
} from '@progress/kendo-react-dropdowns'

const currentYear = dayjs().year()

export interface FiscalYearDropDownListProps {
  /** FiscalYearDropDownList style */
  fyddlStyle?: CSSProperties
  /** Offset on current year to control start year */
  origin?: number
  /** Year selection range */
  range?: number
  /** Year default value */
  fyddlDefaultValue?: number
  /** Year default value */
  fyddlDefaultValueList?: string[]
  /** Year value */
  fyddlValue?: number
  /** Year value */
  fyddlValueList?: string[]
  /** Sets the start year of the FiscalYearDropDownList(must be less than current year). End year is current year */
  startYear?: number
  onYearChange?: (year: number) => void
  onYearChangeList?: (year: string[]) => void
  disabled?: boolean
  clear?: boolean
  popupSettings?: DropDownsPopupSettings
  fyTitle?: string
  className?: string
  multiple?: boolean
}

export const FiscalYearDropDownList: FC<FiscalYearDropDownListProps> = ({
  fyddlStyle,
  origin = 0,
  range = 0,
  fyddlDefaultValue,
  fyddlDefaultValueList,
  fyddlValue,
  fyddlValueList,
  startYear,
  onYearChange,
  onYearChangeList,
  disabled = false,
  clear = false,
  popupSettings,
  fyTitle,
  className,
  multiple = false
}) => {
  const createData = () => {
    if (clear && !multiple) {
      if (startYear !== undefined && startYear < currentYear) {
        const range = currentYear - startYear + 1 + 1

        const years = Array.from({ length: range }).map(
          (_, index) => index > 0 ? startYear + index - 1 : ''
        )

        return years
      }

      const years = Array.from({ length: range + 1 }).map(
        (_, index) => index > 0 ? currentYear + origin + index - 1 : ''
      )

      return years
    } else {
      if (startYear !== undefined && startYear < currentYear) {
        const range = currentYear - startYear + 1

        const years = Array.from({ length: range }).map(
          (_, index) => String(startYear + index)
        )

        return years
      }

      const years = Array.from({ length: range }).map(
        (_, index) => String(currentYear + origin + index)
      )

      return years
    }
  }

  const createValue = (value?: number) => {
    return value === undefined ? null : value
  }

  const createValueList = (valueList?: string[]) => {
    if (!Array.isArray(valueList)) {
      return null
    }
    if (valueList.length > 0) {
      return valueList?.filter((value: string) => value !== undefined)
    } else {
      return valueList
    }
  }

  const handleChange = (event: DropDownListChangeEvent) => {
    onYearChange?.(event.value)
  }

  const handleChangeList = (event: MultiSelectChangeEvent) => {
    let value: string[] = []
    value = event.value.filter((v: string) => v !== undefined)
    onYearChangeList?.(value)
  }

  return multiple
    ? (
        <MultiSelect
          style={fyddlStyle}
          disabled={disabled}
          data={createData()}
          defaultValue={createValueList(fyddlDefaultValueList) as string[]}
          value={createValueList(fyddlValueList) as string[]}
          onChange={handleChangeList}
          popupSettings={popupSettings}
          className={className}
      />
      )
    : (
        <DropDownList
          style={fyddlStyle}
          disabled={disabled}
          data={createData()}
          defaultValue={createValue(fyddlDefaultValue)}
          value={createValue(fyddlValue)}
          onChange={handleChange}
          popupSettings={popupSettings}
          title={fyTitle}
          className={className}
        />
      )
}
